import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { ChartBar, Trophy, Flame, FileText, Receipt, Users } from "@phosphor-icons/react";

/**
 * Phase 4b-3 add-on: Weekly WhatsApp Digest — read-only dashboard widget.
 * Shown on the main /dashboard. Pulls /api/whatsapp/dashboard/weekly-digest.
 * No emails. No external automation. Pure dashboard read.
 */
const WeeklyDigestWidget = ({ user }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const params = new URLSearchParams({
          requester_role: user?.role || "",
          requester_user_id: user?.user_id || "",
        });
        const r = await axios.get(`${API}/whatsapp/dashboard/weekly-digest?${params.toString()}`);
        if (alive) setD(r.data || null);
      } catch {
        // silent — never blocks the dashboard
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user?.role, user?.user_id]);

  if (loading) return null;
  if (!d || (!d.messages_sent && !d.customers_reached)) return null;

  return (
    <div data-testid="weekly-digest-widget" className="bg-white border border-slate-200 mb-8">
      <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between bg-gradient-to-r from-blue-50 to-white">
        <div className="flex items-center gap-2">
          <ChartBar size={16} weight="bold" className="text-blue-700" />
          <h3 className="text-sm uppercase tracking-[0.2em] font-bold text-slate-700">Last Week · Sales Communication</h3>
        </div>
        <span className="text-[10px] uppercase tracking-wider font-bold text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5">7d</span>
      </div>

      <div className="p-5">
        <p className="text-base text-slate-800 leading-relaxed mb-4" data-testid="digest-summary-line">
          Last week your team sent <strong className="text-blue-700 tabular-nums">{d.messages_sent}</strong> WhatsApp message{d.messages_sent !== 1 ? "s" : ""} across <strong className="text-blue-700 tabular-nums">{d.customers_reached}</strong> customer{d.customers_reached !== 1 ? "s" : ""}.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {d.top_performer && (
            <div data-testid="digest-top-performer" className="border border-slate-200 p-3 flex items-start gap-3">
              <Trophy size={20} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Top Performer</div>
                <div className="font-semibold truncate">{d.top_performer.name}</div>
                <div className="text-xs text-slate-500 tabular-nums">{d.top_performer.count} message{d.top_performer.count !== 1 ? "s" : ""}</div>
              </div>
            </div>
          )}
          {d.hottest_customer && (
            <div data-testid="digest-hottest-customer" className="border border-slate-200 p-3 flex items-start gap-3">
              <Flame size={20} weight="fill" className="text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Hottest Lead / Customer</div>
                <div className="font-semibold truncate">{d.hottest_customer.name}</div>
                <div className="text-xs text-slate-500 tabular-nums">{d.hottest_customer.open_count} PDF open{d.hottest_customer.open_count !== 1 ? "s" : ""}</div>
              </div>
            </div>
          )}
          {d.top_template && (
            <div data-testid="digest-top-template" className="border border-slate-200 p-3 flex items-start gap-3">
              <FileText size={20} weight="bold" className="text-emerald-700 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Most Used Template</div>
                <div className="font-semibold truncate">{d.top_template.name}</div>
                <div className="text-xs text-slate-500 tabular-nums">{d.top_template.count} use{d.top_template.count !== 1 ? "s" : ""}</div>
              </div>
            </div>
          )}
          <div data-testid="digest-quotation-count" className="border border-slate-200 p-3 flex items-start gap-3">
            <FileText size={20} weight="bold" className="text-blue-700 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Quotation Messages</div>
              <div className="text-2xl tracking-tighter font-bold tabular-nums" style={{fontFamily: 'Outfit, sans-serif'}}>{d.quotation_messages}</div>
            </div>
          </div>
          <div data-testid="digest-invoice-count" className="border border-slate-200 p-3 flex items-start gap-3">
            <Receipt size={20} weight="bold" className="text-amber-700 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Invoice / Payment Reminders</div>
              <div className="text-2xl tracking-tighter font-bold tabular-nums" style={{fontFamily: 'Outfit, sans-serif'}}>{d.invoice_or_payment_messages}</div>
            </div>
          </div>
          <div className="border border-slate-200 p-3 flex items-start gap-3">
            <Users size={20} weight="bold" className="text-slate-700 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Customers Reached</div>
              <div className="text-2xl tracking-tighter font-bold tabular-nums" style={{fontFamily: 'Outfit, sans-serif'}}>{d.customers_reached}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklyDigestWidget;
